/**
 * Application configuration
 *
 * This file contains environment-specific settings for the application.
 * Edit this file to change API endpoints, feature flags, etc.
 */

const config = {
  // API configuration
  api: {
    // Base URL for all API requests
	  baseUrl: process.env.REACT_APP_API_BASE || "https://api.lt.agency",
    // Endpoints (relative to baseUrl)
    endpoints: {
      auth: {
        login: "/auth/login",
        validate: "/auth/validate",
        checkAuth: "/auth/check-auth",
      },
      slack: {
        madProps: {
          random: "/slack/mad-props/random",
          all: "/slack/madprops",
        },
      },
      admin: {
        checkAdmin: "/admin/check-admin",
        admins: "/admin/admins",
        models: "/admin/models",
        modelsReorder: "/admin/models/reorder",
      },
      dashboard: {
        chatbots: "/dashboard/chatbots",
        models: "/dashboard/models",
        tpAvatar: "/dashboard/tp-avatar",
      },
      chatbot: {
        base: "/chatbot",
        chat: "/chatbot/chat",
        improve: "/chatbot/improve",
        improveStream: "/chatbot/improve/stream",
        sandboxFiles: "/chatbot/sandbox/files",
        sandboxUpload: "/chatbot/sandbox/upload",
        adminUsage: "/chatbot/admin/usage",
        adminMessages: "/chatbot/admin/messages",
      },
      imageGen: {
        models: "/image-gen/models",
        history: "/image-gen/history",
        ws: "/image-gen/ws",
      },
      syntheticAudience: {
        personas: "/synthetic-audience/personas",
        surveys: "/synthetic-audience/surveys",
        ws: "/synthetic-audience/personas/chat",
      },
      goose: {
        export: "/goose/export",
      },
      zoho: {
        submitContactForm: "/zoho/submit-contact-form",
        testConnection: "/zoho/test-connection",
      },
      websiteReport: {
        start: "/website-report/start",
        reports: "/website-report/reports",
      },
      opsDashboard: {
        data: "/ops-dashboard/data",
      },
      lightPark: {
        data: "/light-park-performance-dashboard/data",
        sources: "/light-park-performance-dashboard/sources",
      },
      impressionHomes: {
        startScrape: "/impression-homes/start-scrape",
        startGenerate: "/impression-homes/start-generate",
        startCcm: "/impression-homes/start-ccm",
        ccmPrompts: "/impression-homes/ccm-prompts",
        jobs: "/impression-homes/jobs",
      },
      miroEstimator: {
        copyTemplate: "/miro-estimator/copy-template",
        populateSheet: "/miro-estimator/populate-sheet",
        populateSheetStream: "/miro-estimator/populate-sheet-stream",
      },
      pdfAccessibility: {
        start: "/pdf-accessibility/start",
        jobs: "/pdf-accessibility/jobs",
        cancel: "/pdf-accessibility/cancel",
        estimate: "/pdf-accessibility/estimate",
      },
      basecampFranchiseReporting: {
        start: "/basecamp-franchise-reporting/start",
        cancel: "/basecamp-franchise-reporting/cancel",
        current: "/basecamp-franchise-reporting/current",
        jobs: "/basecamp-franchise-reporting/jobs",
      },
      claudeAgent: {
        agentsAvailable: "/claude-agent/agents/available",
        agents: "/claude-agent/agents",
        agent: "/claude-agent/agents/:id",
        adminAgents: "/claude-agent/admin/agents",
        threads: "/claude-agent/threads",
        // template strings — callers fill in :id
        thread: "/claude-agent/threads/:id",
        threadEvents: "/claude-agent/threads/:id/events",
        threadMessages: "/claude-agent/threads/:id/messages",
        threadInterrupt: "/claude-agent/threads/:id/interrupt",
        threadToolConfirmation: "/claude-agent/threads/:id/tool-confirmation",
        threadFiles: "/claude-agent/threads/:id/files",
        // WebSockets (use ws(s):// in callers)
        wsThreadStream: "/claude-agent/threads/:id/stream",
        wsUserStatus: "/claude-agent/user/status",
        environments: "/claude-agent/environments",
        environment: "/claude-agent/environments/:id",
        environmentFiles: "/claude-agent/environments/:id/files",
        envFileUpload: "/claude-agent/environments/:id/files/upload",
        envFileDownload: "/claude-agent/environments/:id/files/download",
        skills: "/claude-agent/skills",
        skill: "/claude-agent/skills/:id",
        skillActive: "/claude-agent/skills/:id/active",
        skillTemplates: "/claude-agent/skill-templates",
        models: "/claude-agent/models",
        adminModels: "/claude-agent/admin/models",
        adminModel: "/claude-agent/admin/models/:id",
        adminModelOrder: "/claude-agent/admin/models/order",
        usageLimit: "/claude-agent/usage/limit",
        adminUsage: "/claude-agent/admin/usage",
        adminMessages: "/claude-agent/admin/messages",
        adminSpendLimit: "/claude-agent/admin/spend-limit",
        migrateEnvTools: "/claude-agent/admin/agents/:id/migrate-env-tools",
        environmentMemory: "/claude-agent/environments/:id/memory",
        environmentWarm: "/claude-agent/environments/:id/warm",
        envMcpServers: "/claude-agent/environments/:id/mcp-servers",
        envMcpServer: "/claude-agent/environments/:envId/mcp-servers/:serverId",
        mcpServerIcon: "/claude-agent/mcp/servers/icon",
        mcpOauthInitiate: "/claude-agent/mcp/oauth/initiate",
        mcpConnectionEnabled: "/claude-agent/mcp/connections/enabled",
        mcpConnectionDisconnect: "/claude-agent/mcp/connections/disconnect",
        envScheduledTasks: "/claude-agent/environments/:id/scheduled-tasks",
        envScheduledTask:
          "/claude-agent/environments/:envId/scheduled-tasks/:taskId",
        envScheduledTaskRun:
          "/claude-agent/environments/:envId/scheduled-tasks/:taskId/run",
        envScheduledTaskRuns:
          "/claude-agent/environments/:envId/scheduled-tasks/:taskId/runs",
      },
      tpHistory: {
        status: "/tp-history/status",
        filters: "/tp-history/filters",
        stories: "/tp-history/stories",
        story: "/tp-history/stories/:id",
        flags: "/tp-history/flags",
        flagSeen: "/tp-history/stories/:id/flags/seen",
        report: "/tp-history/report",
      },
    },
  },

  // External workflows (n8n)
  n8n: {
    suggestion: {
      // Optionally override via REACT_APP_N8N_STEP1_URL / REACT_APP_N8N_STEP2_URL
      step1Url:
        process.env.REACT_APP_N8N_STEP1_URL ||
        "https://n8n.lt.agency/webhook/0b040024-b4e4-4f02-a958-d068fe23ff60",
      step2Url:
        process.env.REACT_APP_N8N_STEP2_URL ||
        "https://n8n.lt.agency/webhook/ce3157eb-97cf-456f-b6b9-ad8097bdb922",
    },
  },

  // Feature flags
  features: {
    enableMadProps: true,
  },
};

export default config;
